import {
  WizValidationErrors,
  WizValidatorFn,
  WizValidators,
} from './../../../../services/validators/wiz-validators';
import {
  Component,
  Input,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
  Renderer2,
  forwardRef,
  ViewContainerRef,
  Output,
  EventEmitter,
} from '@angular/core';
import { WizTranslatableComponent } from '../../../core/wiz-translatable-component';
import {
  BASE_COMPONENT,
  WizBaseComponent,
} from '../../../core/wiz-base-component';
import { WizLabel } from '../../wiz-label/wiz-label';
import { MatIcon } from '@angular/material/icon';
import { IWIZ_COMPONENT} from '../../../../interfaces/iwiz-component';
import { WIZ_ERROR_MESSAGE, WIZ_EVENT_TYPE } from '@wizeoo/akm-core-lib';
import { WizEvent } from '../../../../events/wiz-event';
import { WizEventManager } from '../../../../events/wiz-event.manager';
import { WizTranslableProperty } from '../../../../services/translation/wiz-translatable-property.decorator';

@Component({
  selector: 'wiz-text-input',
  templateUrl: '../wiz-input.html', // superClass pour TextInput et NumericInput
  styleUrls: ['../wiz-input.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: BASE_COMPONENT, useExisting: forwardRef(() => WizTextInput) },
    { provide: WizBaseComponent, useExisting: forwardRef(() => WizTextInput) },
    { provide: IWIZ_COMPONENT, useExisting: forwardRef(() => WizTextInput) },
  ],
})
export class WizTextInput extends WizTranslatableComponent {
  constructor(
    protected cdref: ChangeDetectorRef,
    public elementRef: ElementRef,
    public renderer: Renderer2
  ) {
    super(cdref, elementRef, renderer);
  }

  // pour traçabilité
  public mode: string = 'textInput';

  // le composant input véritable
  @ViewChild('innerInput', { static: true, read: ElementRef })
  protected _wizInput: ElementRef;

  // label du compo (en haut)
  @ViewChild('innerLabel', { read: WizLabel, static: true })
  protected innerLabel: WizLabel;
  // l'astérisque
  @ViewChild('innerAsterisk', { static: true, read: ElementRef })
  protected innerAsterisk: ElementRef;

  // icone de gauche
  @ViewChild('innerLeftIcon', { static: false, read: MatIcon })
  protected innerLeftIcon: MatIcon;
  // croix pour clear du compo
  @ViewChild('innerClearIcon', { static: false, read: MatIcon })
  protected innerClearIcon: MatIcon;
  // icone de droite
  @ViewChild('innerRightIcon', { static: false, read: MatIcon })
  protected innerRightIcon: MatIcon;
  // password visibility icon
  @ViewChild('innerPasswordIcon', { static: false, read: MatIcon })
  protected innerPasswordIcon: MatIcon;

  // label pour affichage du hint
  @ViewChild('innerhint', { read: WizLabel, static: true })
  protected innerhint: WizLabel;
  // container en bas pour injection message d'erreur
  @ViewChild('errorContainer', { read: ViewContainerRef, static: true })
  protected errorContainer: ViewContainerRef;

  // ####
  // ####
  // #### configuration ####
  // ####
  // ####

  private _wizInputType: string = 'text'; // type d'input : text / password / email / phone
  @Input()
  public set wizInputType(pValue: string) {
    this.setWizInputType(pValue);
  }
  public get wizInputType(): string {
    return this.getWizInputType();
  }
 
  /**
   * methodes getter/setter surchargeables du _wizInputType
   */
  protected setWizInputType(pValue: string) {
    this._wizInputType = pValue;
    this._innerInputType = pValue;
    this.cdref.markForCheck();
  }
  protected getWizInputType(): string {
    return this._wizInputType;
  }

  private _wizShowClearButton: boolean = false; // afficher le bouton "clear"
  @Input()
  public set wizShowClearButton(v: boolean) {
    this.setWizShowClearButton(v);
  }
  public get wizShowClearButton(): boolean {
    return this.getWizShowClearButton();
  }
  /**
   * methodes getter/setter surchargeables du _wizShowClearButton
   */
  protected setWizShowClearButton(v: boolean) {
    this._wizShowClearButton = v;
    this.cdref.markForCheck();
  }
  protected getWizShowClearButton(): boolean {
    return this._wizShowClearButton;
  }
  /** affichage de l'icone clear : si souhaité, pas disabled, et donnée */
  public showClearIcon(): boolean {
    return this.wizShowClearButton && !!this.wizInputValue;
  }

  /** accesseur sur la valeur actuelle du composant de saisie */
  protected get wizInputValue(): string {
    return this._wizInput ? this._wizInput.nativeElement.value : null;
  }

  private _wizMaxChars: number; // nb char max.
  @Input()
  public set wizMaxChars(v: number) {
    this.setWizMaxChars(v);
  }
  public get wizMaxChars(): number {
    return this.getWizMaxChars();
  }
  /**
   * methodes getter/setter surchargeables du _wizMaxChars
   */
  protected setWizMaxChars(v: number) {
    this._wizMaxChars = v;
    this.cdref.markForCheck();
  }
  protected getWizMaxChars(): number {
    return this._wizMaxChars;
  }

  private _wizRightIcon: string; // icone de droite, matIcon (string)
  @Input()
  public set wizRightIcon(v: string) {
    this.setWizRightIcon(v);
  }
  public get wizRightIcon(): string {
    return this.getWizRightIcon();
  }
  /**
   * methodes getter/setter surchargeables du _wizRightIcon
   */
  public setWizRightIcon(v: string) {
    this._wizRightIcon = v;
    this.cdref.markForCheck();
  }
  public getWizRightIcon(): string {
    return this._wizRightIcon;
  }

  private _wizLeftIcon: string; // icone de gauche
  @Input()
  public set wizLeftIcon(v: string) {
    this.setWizLeftIcon(v);
  }
  public get wizLeftIcon(): string {
    return this.getWizLeftIcon();
  }
  /**
   * methodes getter/setter surchargeables du _wizLeftIcon
   */
  protected setWizLeftIcon(v: string) {
    this._wizLeftIcon = v;
    this.cdref.markForCheck();
  }
  protected getWizLeftIcon(): string {
    return this._wizLeftIcon;
  }

  private _wizShowErrors: boolean = false; // afficher les erreurs sous le bouton
  // attention, le composant doit porter la directive WizValidator pour être pris en compte
  @Input()
  public set wizShowErrors(v: boolean) {
    this.setWizShowErrors(v);
  }
  public get wizShowErrors(): boolean {
    return this.getWizShowErrors();
  }
  /**
   * methodes getter/setter surchargeables du _wizShowErrors
   */
  protected setWizShowErrors(v: boolean) {
    this._wizShowErrors = v;
    this.cdref.markForCheck();
  }
  protected getWizShowErrors(): boolean {
    return this._wizShowErrors;
  }
  // prompt traduit, injecté depuis wizPlaceholder
  // translatedPlaceholder est initialisé par une chaine vide pour éviter d'afficher undefined
  public translatedPlaceholder: string = '';
  private _wizPlaceholder: string;
  @WizTranslableProperty('translatedPlaceholder') // décorateur de méthode pour traduction du prompt
  @Input()
  public set wizPlaceholder(str: string) {
    this._wizPlaceholder = str;
  }
  public get wizPlaceholder(): string {
    return this._wizPlaceholder;
  }

  private _wizHint: string; // le hint, sera traduit par son wizLabel
  @Input() public set wizHint(v: string) {
    this.setWizHint(v);
  }
  public get wizHint(): string {
    return this.getWizHint();
  }
  /**
   * methodes getter/setter surchargeables du _wizHint
   */
  protected setWizHint(v: string) {
    this._wizHint = v;
    this.cdref.markForCheck();
  }
  protected getWizHint(): string {
    return this._wizHint;
  }

  /**
   * On set la wizvalue en considérant que la valeur est valide
   */
  public setWizValue(value: any) {
    super.setWizValue(value);
    // TO_VALIDATE : before
    // this._wizInitialValue = value;
    // this._lastGoodValue = value;//feed initial pour rollback

    // TO_VALIDATE : After
    // Explications  _wizInitialValue initialiser sur la prise de focus
    if (!this._lastGoodValue) {
      // alimentation initiale
      this._lastGoodValue = value;
    }
    // if(this.mode === 'numericInput') {
    //   console.log('TI setWizValue', value, this._lastGoodValue);
    // }

    if (!this._componentIsActive) {
      // check _componentIsActive pour ne pas afficher d'erreur quand la valeur est setter par programmation
      this._hasErrors = false;
    } else if (this.wizMandatory && (value === null || value === undefined)) {
      // pas de valeur et mandatory
      this._hasErrors = true;
    } else {
      this._hasErrors = false;
    }

    // this._hasErrors = false; // pas d'erreur sur init prog
    this.manageErrorDisplay(); // reset de l'erreur si existe
    this.cdref.markForCheck();
  }

  /** Valeur de l'input interne */
  public getInnerInputValue(): string {
    return this.getWizValue();
  }

  @Output()
  wizChange: EventEmitter<string> = new EventEmitter<string>();

  /**
   * pour le numericInput, détection d'une saisie pour activer les boutons save / delete
   */
  @Output()
  wizKey: EventEmitter<string> = new EventEmitter<string>();


  private _readonly: boolean = false; // lecture seule sans disable
  @Input()
  public set readonly(pValue: boolean) {
    this._readonly = pValue;
  }
  public get readonly(): boolean {
    return this._readonly;
  }


  // ####
  // ####
  // #### mécanique interne ####
  // ####
  // ####

  // pose des restrictions de saisie / copie
  public ngOnInit(): void {
    super.ngOnInit();
    WizEventManager.addListener(
      this,
      this._wizInput,
      'keypress',
      (e: KeyboardEvent) => this.onKeyPress(e)
    );
    WizEventManager.addListener(
      this,
      this._wizInput,
      'paste',
      (e: ClipboardEvent) => this.onPaste(e)
    );
  }

  // Suppression des restrictions de saisie / copie
  public ngOnDestroy(): void {
    super.ngOnDestroy();
    WizEventManager.removeListener(this, this._wizInput, 'keypress');
    WizEventManager.removeListener(this, this._wizInput, 'paste');
  }

  /**
   * Numeric input restriction on key press
   * @param e Keyboard event
   */
  public onKeyPress(e: KeyboardEvent) {
    // Les caractères spéciaux ne génèrent pas de keypress
    this.preventInsert(e.key, e);
  }

  /**
   * appeler lors d'un coller dans l'input
   * cette méthode récupère le texte collé et appelle la méthode wizPreventInsert
   * On écoute l'évenement paste en plus du keydown ctrl + v || shift + Inser, car un coller est déclenché par le clavier ou la souris
   * @param event, Paste event
   */
  public onPaste(event: ClipboardEvent) {
    // le caractère en cours
    const inputChar = event.clipboardData.getData('Text');
    this.preventInsert(inputChar, event);
  }

  /**
   * Annule l'insertion d'un caractère ou chaine de caractère
   * Si cette valeur est invalide par rapport à numericRegExp
   * @params value, le caratère ou la chaine à inserer
   * @params event, l'event à prevent si value n'est pas autorisé
   */
  public preventInsert(value: string, event: Event) {
    // La chaine existante
    const currentString: string = this.innerInputValue;
    // la future chaine
    const selectionStart: number = this.wizInput.nativeElement.selectionStart;
    const selectionEnd: number = this.wizInput.nativeElement.selectionEnd;
    const nextString: string =
      currentString.substr(0, selectionStart) +
      value +
      currentString.substr(selectionEnd);
    if (!!this.wizRestrict) {
      // seulement si le restrict est actif
      // on teste
      if (nextString && !String(nextString).match(this.wizRestrict)) {
        event.preventDefault();
      }
    }
  }

  /**  accesseur sur l'input physique */
  protected get wizInput(): ElementRef {
    return this._wizInput;
  }

  // affichage de l'icone de visibilité des mdps
  public get showPasswordIcon(): boolean {
    return this._wizInputType === 'password';
  }

  // quelle icone afficher ?
  public get passwordIcon(): string {
    return this._wizHidePassword ? 'see' : 'see';
  }

  // type appliqué à l'input interne (switch sur visible ou pas)
  private _innerInputType: string = 'text';
  public get innerInputType(): string {
    return this._innerInputType;
  }

  /** email input type flag */
  private isEmailInput(): boolean {
    return this._wizInputType === 'email';
  }

  // either the text is hidden or not when the input type is password
  private _wizHidePassword: boolean = true;

  public get wizHidePassword(): boolean {
    return this._wizHidePassword;
  }

  /** changement de visibilité du mdp */
  public switchPasswordType(): void {
    if (this._wizInputType === 'password' && !this.getWizDisabled()) {
      // seulement si le compo est password est pas disabled
      this._wizHidePassword = !this._wizHidePassword;
      this._innerInputType = this._wizHidePassword ? 'password' : 'text';
    }
  }

  /** donne le focus à l'input depuis FocusManager */
  public wizFocus(): void {
    this.wizInput.nativeElement.select(); // select pour selectionner le contenu de l'input lors du focus
  }

  /**
   * donne le focus à l'input depuis FocusManager
   *
   * @param value valeur à injecter
   */
  public wizFocusInjector(value: string): void {
    if (!this.wizDisabled) {
      // controle sur base de la regExp
      if ( value && (!this.wizRestrict || (this.wizRestrict && value.match(this.wizRestrict)))) { // restrict éventuel
        this.wizInput.nativeElement.select(); // select pour selectionner le contenu de l'input lors du focus
        this.wizValue = value;
      } else {
        this.wizFocus();
        // console.log('wizFocusWithValue nope', this.innerInput.nativeElement.value);
      }
    }
  }

  /**
   * Prise de focus, pour surcharge
   * @param event Evenement
   */
  public onFocus(event): void {
    if (!this._componentIsActive) {
      // annulée sur focusOut
      this._componentIsActive = true;
      this._wizInitialValue = this.getWizValue();
    }
    this.wizGetFocus();
  }

  /**
   * Perte de focus, pour surcharge
   * @param event Evenement focusout
   */
  public onBlur(event: FocusEvent): void {
    this._wizInitialValue = null;
    this._componentIsActive = false;
    this.wizLoseFocus();
  }

  /**
   * Handler de clic dans l'input, pour surcharge
   * @param event Evenement clic
   */
  public inputClic(event: MouseEvent) {}

  /**
   * accès direct à la valeur de l'input physique (pour rollback keyboard, Prise de taille..)
   * utilisé dans Numeric et TextArea
   */
  protected get innerInputValue(): string {
    let result: string;
    if (this.wizInput && this.wizInput.nativeElement) {
      result = this.wizInput.nativeElement.value;
    }
    return result;
  }

  // ####
  // ####
  // #### gestion de la valeur et des erreurs ####
  // ####
  // ####/
  protected _componentIsActive: boolean = false; // le focus est pris (car initialValue peut être null)
  protected _wizInitialValue: string = null; // initiale , alimentée sur set WizValue

  protected _hasErrors: boolean = false; // le composant a à minima une erreur
  public get hasErrors(): boolean {
    return this._hasErrors;
  }

  /**
   * event primitif ngModelChange sur input
   * @param value, chaine de caractère saisie
   */
  public onChange(value: string) {
    this._wizValue = value;
    this._hasErrors = false; // suppression de l'erreur
    this.manageErrorDisplay(); // gestion du rendu (suppression du marquage physique de l'erreur)

    this.wizChange.emit(this._wizValue);
    WizEventManager.dispatchEvent(
      this.elementRef,
      new WizEvent(WIZ_EVENT_TYPE.CHANGE, this, this._wizValue)
    );
  }

  /**
   * Clear wizValue
   * @param: e Event. Ne sert actuellement pas mais utile pour un traiement spécifique sur la target
   */
  public onClear(e: Event) {
    if (!this.getWizDisabled()) {
      // TO_VALIDATE : ADD
      // Clear de la wiz value
      // Le choix de supprimer la wizValue car l'icon sera principalement pour des filtres de recherche
      this.onChange(null);
      WizEventManager.dispatchEvent(
        this.elementRef,
        new WizEvent(WIZ_EVENT_TYPE.CLEAR, this, this._wizValue)
      );
      // Donne le focus à l'input
      this.wizFocus();
    }

    // garder le code ci dessous dans le cas ou le onClear devrait servir à retourner à la valeur d'origine
    // this.onChange(this.getLastGoodValue()); // recyclage onChange
  }

  /**
   * Appel externe sur focusOut depuis la directive wizValidator
   * @param pErrors la collection d'erreur
   * @param wizShowErrors : //TODO JRO demande externe ? sert à rien ici
   */
  public onWizValidation(
    pErrors: WizValidationErrors,
    wizShowErrors: boolean
  ): void {
    this._hasErrors = pErrors.length > 0;
    this.manageErrorDisplay();
  }

  /** suppression / affichage des marqueurs d'erreur & messages */
  protected manageErrorDisplay(): void {
    if (this._hasErrors) {
    } else {
      if (this.getErrorVCRef()) {
        // clear container
        this.getErrorVCRef().clear();
      }
    }
    this.cdref.markForCheck();
  }

  // ####
  // ####
  // #### fonctions complémentaires sur boutons ####
  // ####
  // ####/

  /**
   * event transmission when the left icon is clicked
   */
  public wizOnLeftIconClicked(): void {
    if (!this.getWizDisabled()) {
      WizEventManager.dispatchEvent(
        this.elementRef,
        new WizEvent(WIZ_EVENT_TYPE.INPUT_LEFT_CLICK, this, this._wizValue)
      );
    }
  }

  /**
   * event transmission when the right icon is clicked
   */
  public wizOnRightIconClicked(): void {
    if (!this.getWizDisabled()) {
      WizEventManager.dispatchEvent(
        this.elementRef,
        new WizEvent(WIZ_EVENT_TYPE.INPUT_RIGHT_CLICK, this, this._wizValue)
      );
    }
  }

  // ####
  // ####
  // #### contraintes ####
  // ####
  // ####

  private _wizRestrict: RegExp; // expression régulière de limitation de saisie
  @Input()
  public set wizRestrict(v: RegExp) {
    this.setWizRestrict(v);
  }
  public get wizRestrict(): RegExp {
    return this.getWizRestrict();
  }

  protected setWizRestrict(v: RegExp) {
    this._wizRestrict = v;
    this.cdref.markForCheck();
  }
  protected getWizRestrict(): RegExp {
    return this._wizRestrict;
  }

  private _wizPattern: string | RegExp; // expression régulière
  @Input()
  public set wizPattern(v: string | RegExp) {
    this.setWizPattern(v);
  }
  public get wizPattern(): string | RegExp {
    return this.getWizPattern();
  }
  /**
   * methodes getter/setter surchargeables du _wizPattern
   */
  protected setWizPattern(v: string | RegExp) {
    this._wizPattern = v;
    this.cdref.markForCheck();
  }
  protected getWizPattern(): string | RegExp {
    return this._wizPattern;
  }

  private _wizMaxlength: number; // taille max
  @Input()
  public set wizMaxlength(v: number) {
    this.setWizMaxlength(v);
  }
  public get wizMaxlength(): number {
    return this.getWizMaxlength();
  }
  /**
   * methodes getter/setter surchargeables du _wizMaxlength
   */
  protected setWizMaxlength(v: number) {
    this._wizMaxlength = v;
    this.cdref.markForCheck();
  }
  protected getWizMaxlength(): number {
    return this._wizMaxlength;
  }

  private _wizMinlength: number; // taille min
  @Input()
  public set wizMinlength(v: number) {
    this.setWizMinlength(v);
  }
  public get wizMinlength(): number {
    return this.getWizMinlength();
  }
  /**
   * methodes getter/setter surchargeables du _wizMinlength
   */
  public setWizMinlength(v: number) {
    this._wizMinlength = v;
    this.cdref.markForCheck();
  }
  public getWizMinlength(): number {
    return this._wizMinlength;
  }
  /**
   * Required validator, overriden by numeric input
   * @param errorMessage Error message key
   */
  protected getRequiredValidator(errorMessage: string): WizValidatorFn {
    return WizValidators.wizRequired(errorMessage);
  }

  /** Component's required error translation key */
  private _wizRequiredErrorMessage: string;
  @Input() public set wizRequiredErrorMessage(str: string) {
    this.setWizRequiredErrorMessage(str);
  }
  public get wizRequiredErrorMessage(): string {
    return this.getWizRequiredErrorMessage();
  }
  /**
   * methodes getter/setter surchargeables du _wizRequiredErrorMessage
   */
  protected setWizRequiredErrorMessage(str: string) {
    this._wizRequiredErrorMessage = str;
    this.cdref.markForCheck();
  }
  protected getWizRequiredErrorMessage(): string {
    return this._wizRequiredErrorMessage;
  }
  /** Component's email error translation key */
  private _wizEmailErrorMessage: string;
  @Input() public set wizEmailErrorMessage(str: string) {
    this.setWizEmailErrorMessage(str);
  }
  public get wizEmailErrorMessage(): string {
    return this.getWizEmailErrorMessage();
  }
  /**
   * methodes getter/setter surchargeables du _wizEmailErrorMessage
   */
  protected setWizEmailErrorMessage(str: string) {
    this._wizEmailErrorMessage = str;
    this.cdref.markForCheck();
  }
  protected getWizEmailErrorMessage(): string {
    return this._wizEmailErrorMessage;
  }

  /** Compoinent's minlength error translation key */
  private _wizMinlengthErrorMessage: string;
  @Input() public set wizMinlengthErrorMessage(str: string) {
    this.setWizMinlengthErrorMessage(str);
  }
  public get wizMinlengthErrorMessage(): string {
    return this.getWizMinlengthErrorMessage();
  }
  /**
   * methodes getter/setter surchargeables du _wizMinlengthErrorMessage
   */
  protected setWizMinlengthErrorMessage(str: string) {
    this._wizMinlengthErrorMessage = str;
    this.cdref.markForCheck();
  }
  public getWizMinlengthErrorMessage(): string {
    return this._wizMinlengthErrorMessage;
  }

  /** Component's maxlength error translation key */
  private _wizMaxlengthErrorMessage: string;
  @Input() public set wizMaxlengthErrorMessage(str: string) {
    this.setWizMaxlengthErrorMessage(str);
  }
  public get wizMaxlengthErrorMessage(): string {
    return this.getWizMaxlengthErrorMessage();
  }
  /**
   * methodes getter/setter surchargeables du _wizMaxlengthErrorMessage
   */
  protected setWizMaxlengthErrorMessage(str: string) {
    this._wizMaxlengthErrorMessage = str;
    this.cdref.markForCheck();
  }
  public getWizMaxlengthErrorMessage(): string {
    return this._wizMaxlengthErrorMessage;
  }

  /** Component's regexp error translation key */
  private _wizPatternErrorMessage: string;
  @Input() public set wizPatternErrorMessage(str: string) {
    this.setWizPatternErrorMessage(str);
  }
  public get wizPatternErrorMessage(): string {
    return this.getWizPatternErrorMessage();
  }
  /**
   * methodes getter/setter surchargeables du _wizPatternErrorMessage
   */
  protected setWizPatternErrorMessage(str: string) {
    this._wizPatternErrorMessage = str;
    this.cdref.markForCheck();
  }
  public getWizPatternErrorMessage(): string {
    return this._wizPatternErrorMessage;
  }

  /** Error message container : contrat d'interface pour la directive wizValidator */
  public getErrorVCRef(): ViewContainerRef {
    return this.errorContainer;
  }

  /** contrat d'interface pour la directive wizValidator */
  public getValidators(): WizValidatorFn[] {
    const validators: WizValidatorFn[] = []; // tableau de fonctions de validation

    // if wizMandatory is true, the required validator in added to wizValidatorsArray
    if (this.wizMandatory) {
      const errorMessage = this.wizRequiredErrorMessage
        ? this.wizRequiredErrorMessage
        : WIZ_ERROR_MESSAGE.REQUIRED;
      validators.push(this.getRequiredValidator(errorMessage));
    }
    // if wizMinlength is gived, the minLength validator in added to wizValidatorsArray
    if (this.wizMinlength && this.wizMinlength > 0) {
      const errorParams = [this.wizMinlength];
      const errorMessage = this.wizMinlengthErrorMessage
        ? this.wizMinlengthErrorMessage
        : WIZ_ERROR_MESSAGE.MIN_LENGTH;
      validators.push(
        WizValidators.wizMinlength(this.wizMinlength, errorMessage, errorParams)
      );
    }
    // if wizMaxlength is true, the maxlength validator in added to wizValidatorsArray
    if (this.wizMaxlength && this.wizMaxlength > 0) {
      const errorMessage = this.wizMaxlengthErrorMessage
        ? this.wizMaxlengthErrorMessage
        : WIZ_ERROR_MESSAGE.MAX_LENGTH;
      const errorParams = [this.wizMaxlength];
      validators.push(
        WizValidators.wizMaxLength(this.wizMaxlength, errorMessage, errorParams)
      );
    }
    // if wizEmailType is true, the email validator in added to wizValidatorsArray
    if (this.isEmailInput()) {
      const errorMessage = this.wizEmailErrorMessage
        ? this.wizEmailErrorMessage
        : WIZ_ERROR_MESSAGE.EMAIL;
      validators.push(WizValidators.wizEmail(errorMessage));
    }
    // Pattern not metched
    if (this.wizPattern) {
      const errorMessage = this.wizPatternErrorMessage
        ? this.wizPatternErrorMessage
        : WIZ_ERROR_MESSAGE.PATTERN;
      validators.push(WizValidators.wizPattern(this.wizPattern, errorMessage));
    }
    return validators;
  }
}
